import { useEffect, useRef, useState } from 'react';
import { Mail, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  fetchFacilitiesContacts, getDisplayErrorMessage, saveFacilitiesContacts,
  type FacilitiesContactsView,
} from '@/lib/api';
import {
  DELIVERY_OPTIONS, FACILITIES_ANCHOR, NEW_ROW_DELIVERY, ROLE_OPTIONS, checkDraft, deliveryLabel, roleLabel, sourceLabel,
  type ContactDraft,
} from '@/lib/facilitiesContacts';
import formStyles from './Form.module.css';
import styles from './FacilitiesContactsCard.module.css';

/**
 * Facilities contacts for one location (Warren, 28 Oct 2026, item 6): "Each
 * location gets a facilities contact list (name, email; editable by the site
 * owner and EVI staff in Admin)." The people emailed when a charger here goes
 * offline, comes back online, or keeps refusing starts. With nobody listed
 * those emails go to EV Initiative operations only, and the card says so.
 *
 * Linked from email: /locations/:id/facilities (and #facilities) open the
 * location page scrolled here. Edit is offered on the API's own can_edit
 * (location:write and the location in the caller's scope), never on persona,
 * so nobody sees a Save that 403s. A caller the API refuses (a fleet admin, a
 * sales partner) sees nothing.
 *
 * Each contact shows and edits its role (Property manager, Concierge,
 * Facilities, Owner, Other); a new row has none until one is chosen. And how
 * often they hear about outages (predictive maintenance stage 4): right away,
 * a daily digest at 8 am, only if down more than 1, 4 or 24 hours, or off.
 * They can also change that themselves from the link in every alert email.
 */
interface FacilitiesContactsCardProps {
  locationId: string;
  /** Scroll here once loaded (the emailed link). */
  focus?: boolean;
}

const emptyRow = (): ContactDraft => ({ name: '', email: '', role: '', alert_delivery: NEW_ROW_DELIVERY });

export function FacilitiesContactsCard({ locationId, focus = false }: FacilitiesContactsCardProps) {
  const [view, setView] = useState<FacilitiesContactsView | null>(null);
  const [hidden, setHidden] = useState(false);
  const [loadErr, setLoadErr] = useState('');
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState<ContactDraft[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    setView(null);
    setHidden(false);
    setLoadErr('');
    fetchFacilitiesContacts(locationId)
      .then((v) => { if (!cancelled) setView(v); })
      .catch(async (err: unknown) => {
        if (cancelled) return;
        const status = (err as { response?: { status?: number } })?.response?.status;
        // 403: this caller is not the site's team or EVI staff; show nothing.
        if (status === 403) { setHidden(true); return; }
        setLoadErr(await getDisplayErrorMessage(err, 'Could not load the facilities contacts.'));
      });
    return () => { cancelled = true; };
  }, [locationId]);

  // The emailed link lands here: scroll once the card has its height.
  useEffect(() => {
    const wanted = focus || window.location.hash === `#${FACILITIES_ANCHOR}`;
    if (wanted && (view || loadErr) && ref.current) ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [focus, view, loadErr]);

  if (hidden) return null;

  const startEdit = (): void => {
    if (!view) return;
    setRows(view.contacts.length ? view.contacts.map((c) => ({ name: c.name, email: c.email, role: c.role, alert_delivery: c.alert_delivery })) : [emptyRow()]);
    setEditing(true);
    setError('');
    setSaved(false);
  };

  const save = async (): Promise<void> => {
    const check = checkDraft(rows);
    if (!check.ok) { setError(check.error); return; }
    setSaving(true);
    setError('');
    try {
      const next = await saveFacilitiesContacts(locationId, check.contacts);
      setView(next);
      setEditing(false);
      setSaved(true);
    } catch (err) {
      setError(await getDisplayErrorMessage(err, 'Save failed. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const setRow = (i: number, patch: Partial<ContactDraft>): void => {
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  };

  const max = view?.max ?? 20;

  return (
    <section id={FACILITIES_ANCHOR} ref={ref} className={styles.card} aria-labelledby="facilities-contacts-title">
      <div className={styles.titleRow}>
        <h2 id="facilities-contacts-title" className={styles.title}>
          <Mail size={15} /> Facilities contacts
        </h2>
        {view?.can_edit && !editing && (
          <button type="button" className={formStyles.btnSecondary} onClick={startEdit}>
            <Pencil size={13} /> Edit
          </button>
        )}
      </div>
      <p className={styles.hint}>
        When a charger here goes offline, comes back online, or keeps refusing starts, we email these
        people and EV Initiative operations. Name, email and role only.
      </p>

      {loadErr && <div className={styles.alertErr}>{loadErr}</div>}
      {!view && !loadErr && <p className={styles.hint}>Loading...</p>}
      {saved && !error && <div className={styles.alertOk}>Saved.</div>}

      {view && !editing && (
        view.contacts.length === 0 ? (
          <p className={styles.opsNote}>
            No contacts yet. Alerts for this site go to EV Initiative operations only
            {view.can_edit ? ' until you add someone.' : '.'}
          </p>
        ) : (
          <ul className={styles.list}>
            {view.contacts.map((c) => (
              <li key={c.email.toLowerCase()} className={styles.item}>
                <span className={styles.who}>
                  <span className={styles.name}>{c.name || c.email}</span>
                  {c.name && <span className={styles.email}>{c.email}</span>}
                </span>
                <span className={styles.meta}>
                  <span className={styles.role}>{roleLabel(c.role)}</span>
                  <span className={styles.source}>{deliveryLabel(c.alert_delivery)}</span>
                  <span className={styles.source}>{sourceLabel(c.source)}</span>
                </span>
              </li>
            ))}
          </ul>
        )
      )}

      {view && editing && (
        <>
          {error && <div className={styles.alertErr} role="alert">{error}</div>}
          <div className={styles.editRows}>
            {rows.map((r, i) => (
              <div key={i} className={styles.editRow}>
                <input
                  className={formStyles.input}
                  aria-label={`Contact ${i + 1} name`}
                  placeholder="Name"
                  value={r.name}
                  maxLength={100}
                  autoComplete="off"
                  onChange={(e) => setRow(i, { name: e.target.value })}
                />
                <input
                  className={formStyles.input}
                  aria-label={`Contact ${i + 1} email`}
                  placeholder="name@example.com"
                  type="email"
                  inputMode="email"
                  value={r.email}
                  maxLength={254}
                  autoComplete="off"
                  onChange={(e) => setRow(i, { email: e.target.value })}
                />
                <select
                  className={formStyles.input}
                  aria-label={`Contact ${i + 1} role`}
                  value={r.role}
                  onChange={(e) => setRow(i, { role: e.target.value as ContactDraft['role'] })}
                >
                  <option value="" disabled>Role</option>
                  {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <select
                  className={formStyles.input}
                  aria-label={`Contact ${i + 1}: how often they hear about outages`}
                  value={r.alert_delivery}
                  onChange={(e) => setRow(i, { alert_delivery: e.target.value as ContactDraft['alert_delivery'] })}
                >
                  {DELIVERY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <button
                  type="button"
                  className={styles.removeBtn}
                  aria-label={`Remove contact ${i + 1}`}
                  onClick={() => setRows((prev) => prev.filter((_, j) => j !== i))}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
          <div className={styles.actions}>
            <button
              type="button"
              className={formStyles.btnSecondary}
              disabled={rows.length >= max}
              onClick={() => setRows((prev) => [...prev, emptyRow()])}
            >
              <Plus size={13} /> Add contact
            </button>
            <button type="button" className={formStyles.btnPrimary} disabled={saving} onClick={() => { void save(); }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button type="button" className={formStyles.btnSecondary} disabled={saving} onClick={() => { setEditing(false); setError(''); }}>
              Cancel
            </button>
            <span className={styles.count}>{rows.length} of {max}</span>
          </div>
          {rows.length === 0 && (
            <p className={styles.opsNote}>Saving an empty list sends this site&apos;s alerts to EV Initiative operations only.</p>
          )}
        </>
      )}
    </section>
  );
}
